import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Mic, MicOff, Lock, Repeat, Zap, Crown, Coins } from "lucide-react";
import api from "@/lib/api";

/* ─── Memberships ─── */
const TIERS = [
  {
    name: "Weekly", price: "15", cadence: "/week",
    tagline: "Try the system. Cancel anytime.",
    grant: { base: 300, bonus: 40, total: 340 },
    creates: "≈ 21 standard videos · 113 images · or 85 voiceovers (1 min)",
    voiceover: { allowed: true, note: "Unlocked · credit-billed" },
    features: [
      "340 credits / week (300 base + 40 bonus)",
      "1080p export, all aspect ratios",
      "Generations debit your credit wallet",
      "No watermark on downloads",
      "Voiceover unlocked",
      "Cancel anytime",
    ],
    cta: "Start Weekly",
  },
  {
    name: "Starter", price: "29.99", cadence: "/month",
    tagline: "For solo creators and small businesses.",
    grant: { base: 600, bonus: 120, total: 720 },
    creates: "≈ 45 standard videos · 240 images · or 180 voiceovers (1 min)",
    voiceover: { allowed: true, note: "Unlocked · credit-billed" },
    features: [
      "720 credits / month (600 base + 120 bonus)",
      "1080p export, all aspect ratios",
      "Generations debit your credit wallet",
      "No watermark on downloads",
      "Voiceover unlocked",
      "Personal commercial license",
    ],
    cta: "Start with Starter",
  },
  {
    name: "Pro", price: "59.99", cadence: "/month",
    tagline: "For high-volume marketers, musicians and agencies.",
    grant: { base: 1200, bonus: 300, total: 1500 },
    creates: "≈ 93 standard videos · 500 images · or 375 voiceovers (1 min)",
    voiceover: { allowed: true, note: "Unlocked · priority TTS queue" },
    features: [
      "1,500 credits / month (1,200 base + 300 bonus)",
      "Priority queue · ~2–3× faster",
      "Multi-Shot 30s/60s commercials · Magazine generator",
      "Music promo presets + audio overlay",
      "Brand kit (logo + colors)",
      "Voiceover · priority queue",
      "Full commercial license",
    ],
    cta: "Choose Pro",
    highlight: true, badge: "Most Popular",
  },
  {
    name: "Premium", price: "99.99", cadence: "/month",
    tagline: "For production houses and serious operators. Bring your own keys.",
    grant: { base: 2000, bonus: 700, total: 2700 },
    creates: "≈ 56 cinema videos (12s) · 540 images · or 675 voiceovers (1 min)",
    voiceover: { allowed: true, note: "ElevenLabs Cinema voice · highest fidelity" },
    features: [
      "2,700 credits / month (2,000 base + 700 bonus)",
      "Bring Your Own Keys — connect OpenAI, Gemini, Anthropic, ElevenLabs",
      "Highest quality (Sora 2 Pro · Cinema)",
      "Express queue · top priority",
      "3 team seats included",
      "Voiceover · Cinema tier",
      "Multi-Shot 30s/60s commercials · Magazine generator",
      "White-label exports",
    ],
    cta: "Go Premium",
  },
];

export default function Pricing() {
  const [view, setView] = useState("memberships");
  const [packs, setPacks] = useState([]);

  // Public packs come from the live pricing endpoint — admin-only packs are
  // filtered out server-side already, so this is safe to render verbatim.
  useEffect(() => {
    api.get("/credits/pricing")
      .then(({ data }) => setPacks((data.packs || []).filter((p) => !p.admin_only)))
      .catch(() => setPacks([]));
  }, []);

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative bg-[#080808] py-24 md:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-5">
            Pricing · USD · Global
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#F4F4F0] leading-[1.02]">
            Two ways to keep creating.
          </h2>
          <p className="mt-6 text-[#A0A0A0] text-lg leading-relaxed">
            Pick what fits your workload. Both lanes give you real, business-ready video — credits never expire, memberships cancel anytime.
          </p>
        </div>

        {/* Decision strip — verbatim founder copy */}
        <div className="grid md:grid-cols-2 gap-4 mb-12" data-testid="landing-pricing-decision">
          <button
            onClick={() => setView("payg")}
            data-testid="landing-decision-payg"
            className={`text-left p-6 md:p-7 border transition-colors ${view === "payg" ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#2A2A2A] hover:border-[#D4AF37]/40 bg-[#0F0F0F]"}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <Zap className={`w-4 h-4 ${view === "payg" ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`} />
              <p className={`text-[10px] uppercase tracking-[0.22em] font-bold ${view === "payg" ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`}>Pay-As-You-Go</p>
            </div>
            <h3 className="font-display text-xl md:text-2xl font-extrabold text-[#F4F4F0] mb-2">Just need one project? Pay as you go.</h3>
            <p className="text-sm text-[#A0A0A0]">Buy a credit pack, render what you need, walk away. Credits never expire. No commitment.</p>
          </button>
          <button
            onClick={() => setView("memberships")}
            data-testid="landing-decision-memberships"
            className={`text-left p-6 md:p-7 border transition-colors ${view === "memberships" ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#2A2A2A] hover:border-[#D4AF37]/40 bg-[#0F0F0F]"}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <Repeat className={`w-4 h-4 ${view === "memberships" ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`} />
              <p className={`text-[10px] uppercase tracking-[0.22em] font-bold ${view === "memberships" ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`}>Membership</p>
            </div>
            <h3 className="font-display text-xl md:text-2xl font-extrabold text-[#F4F4F0] mb-2">Creating often? Membership gives you more downloads, voice, and speed.</h3>
            <p className="text-sm text-[#A0A0A0]">Recurring plan with included downloads, voiceover unlocked, priority queue.</p>
          </button>
        </div>

        {view === "memberships" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5" data-testid="landing-memberships-grid">
            {TIERS.map((tier, i) => (
              <motion.div
                key={tier.name}
                data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`relative flex flex-col p-7 md:p-8 ${tier.highlight ? "bg-[#121212] border border-[#D4AF37] pro-card-glow lg:-translate-y-3" : "bg-[#121212] border border-[#2A2A2A]"}`}
              >
                {tier.badge && (
                  <span data-testid="pro-most-popular-badge" className="absolute -top-3 left-7 bg-[#D4AF37] text-[#080808] text-[10px] font-bold uppercase tracking-[0.22em] px-3 py-1.5">
                    {tier.badge}
                  </span>
                )}
                <h3 className="font-display text-2xl md:text-3xl font-extrabold text-[#F4F4F0] tracking-tight">{tier.name}</h3>
                <p className="mt-2 text-sm text-[#A0A0A0] leading-relaxed min-h-[44px]">{tier.tagline}</p>
                <div className="mt-7 flex items-baseline gap-1">
                  <span className="text-xl text-[#A0A0A0]">$</span>
                  <span className="font-display text-5xl font-black text-[#F4F4F0] tracking-tighter">{tier.price}</span>
                  <span className="text-xs text-[#A0A0A0] ml-1">{tier.cadence}</span>
                </div>
                {tier.grant && (
                  <div className="mt-4 px-3 py-2.5 border border-[#D4AF37]/40 bg-[#D4AF37]/5" data-testid={`landing-grant-${tier.name.toLowerCase()}`}>
                    <div className="flex items-baseline gap-2">
                      <Coins className="w-3.5 h-3.5 text-[#D4AF37]" />
                      <span className="font-display text-lg font-bold text-[#F4F4F0]">{tier.grant.total.toLocaleString()}</span>
                      <span className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">credits total</span>
                    </div>
                    <p className="text-[10px] text-[#D4AF37] mt-0.5">
                      {tier.grant.base.toLocaleString()} base <span className="text-[#5A5A5A]">+</span> +{tier.grant.bonus.toLocaleString()} bonus
                    </p>
                    {tier.creates && <p className="text-[10px] text-[#A0A0A0] mt-1.5 leading-relaxed">{tier.creates}</p>}
                  </div>
                )}
                <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider border ${tier.voiceover.allowed ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5" : "border-red-500/40 text-red-300 bg-red-500/5"}`}>
                  {tier.voiceover.allowed ? <Mic className="w-3 h-3" /> : <MicOff className="w-3 h-3" />}
                  {tier.voiceover.allowed ? "Voiceover unlocked" : "Voiceover locked"}
                </div>
                <p className="text-[10px] text-[#A0A0A0] mt-2">{tier.voiceover.note}</p>
                <Link
                  to="/register"
                  data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                  className={`mt-6 inline-flex items-center justify-center gap-2 font-bold px-5 py-3 text-sm transition-colors ${tier.highlight ? "bg-[#D4AF37] text-[#080808] hover:bg-[#F0C84B]" : "border border-[#2A2A2A] text-[#F4F4F0] hover:border-[#D4AF37]"}`}
                >
                  {tier.cta} <ArrowRight className="w-4 h-4" />
                </Link>
                <ul className="mt-7 space-y-3 border-t border-white/10 pt-7">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-xs md:text-sm text-[#F4F4F0]/90">
                      <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      <span className="leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        ) : (
          <div data-testid="landing-payg-grid">
            <div className="mb-6 border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5 flex items-start gap-4">
              <Coins className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-display text-lg font-bold text-[#F4F4F0]">Credits never expire.</h3>
                <p className="text-xs text-[#A0A0A0] mt-1">
                  Buy a pack once, generate whenever. Failed renders auto-refund. Larger packs = better $/credit. Voiceover is credit-billed once you're on any plan.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {packs.length === 0 ? (
                <div className="col-span-full text-center py-8 text-[#A0A0A0] text-sm">Loading credit packs…</div>
              ) : packs.map((p) => {
                const base    = Number(p.base_credits  ?? p.credits);
                const bonus   = Number(p.bonus_credits ?? 0);
                const total   = Number(p.credits);
                const popular = p.badge === "Most Popular";
                const best    = p.badge === "Best Value";
                return (
                  <div key={p.sku} data-testid={`landing-pack-${p.sku}`} className={`relative border bg-[#121212] p-5 transition-colors ${
                    popular ? "border-[#D4AF37] ring-1 ring-[#D4AF37]/40"
                    : best  ? "border-emerald-500/60 ring-1 ring-emerald-500/30"
                    : "border-[#2A2A2A] hover:border-[#D4AF37]/50"
                  }`}>
                    {p.badge && (
                      <span className={`absolute -top-3 left-4 text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 flex items-center gap-1 ${
                        popular ? "bg-[#D4AF37] text-[#080808]" : "bg-emerald-500 text-[#080808]"
                      }`} data-testid={`landing-pack-badge-${p.sku}`}>
                        {best && <Crown className="w-2.5 h-2.5" />} {p.badge}
                      </span>
                    )}
                    <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">${p.price_usd}</p>
                    <p className="font-display text-3xl font-black text-[#F4F4F0] mt-2">{total.toLocaleString()}</p>
                    <p className="text-[10px] text-[#5A5A5A] uppercase tracking-wider mt-1">credits total</p>
                    {bonus > 0 ? (
                      <p className="text-[10px] text-[#D4AF37] mt-2" data-testid={`landing-pack-bonus-${p.sku}`}>
                        {base.toLocaleString()} base <span className="text-[#5A5A5A]">+</span> <b>+{bonus.toLocaleString()} bonus</b>
                      </p>
                    ) : (
                      <p className="text-[10px] text-[#5A5A5A] mt-2">{base.toLocaleString()} base · no bonus</p>
                    )}
                    <Link
                      to="/register"
                      data-testid={`landing-pack-cta-${p.sku}`}
                      className={`mt-4 inline-flex items-center justify-center gap-1 w-full font-bold text-xs py-2.5 transition-colors ${
                        popular || best ? "bg-[#D4AF37] text-[#080808] hover:bg-[#F0C84B]" : "border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0]"
                      }`}
                    >
                      Buy ${p.price_usd}
                    </Link>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
              <Lock className="w-4 h-4 text-amber-300 mt-0.5" />
              <p className="text-xs text-amber-100/90">
                <span className="font-semibold text-amber-200">Free Trial: voiceover is locked.</span> Add any membership or buy credits to unlock voice generation.
              </p>
            </div>
          </div>
        )}

        <p className="mt-12 text-center text-xs text-[#A0A0A0] tracking-wider">
          Pay-As-You-Go credits never expire · Memberships cancel anytime · Failed renders auto-refund · All plans render with clean English on-screen text
        </p>
      </div>
    </section>
  );
}
